// MobileOpportunityStack — Full-screen swipeable opportunity card stack.
// Swipe right → dismiss. Swipe left → execute (opens action sheet).
// Tap → open GameWorkspaceDrawer for full intelligence.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import clsx from 'clsx';
import { X, Zap, TrendingUp, ChevronLeft, ChevronRight, RotateCcw, Activity } from 'lucide-react';
import { pulseApi } from '../../api/client';
import { useAppStore } from '../../store/useAppStore';
import type { SportRecommendation } from '../../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const SWIPE_THRESHOLD = 80;
const SWIPE_VELOCITY = 400;

const DECISION_STYLES: Record<string, { label: string; cls: string }> = {
  STRONG_BET:    { label: 'STRONG',    cls: 'bg-win/20 text-win border-win/40'           },
  QUALIFIED_BET: { label: 'QUALIFIED', cls: 'bg-brand/20 text-brand-light border-brand/40' },
};

const LEAGUE_STYLES: Record<string, { label: string; cls: string }> = {
  mlb:  { label: 'MLB',  cls: 'bg-brand/15 text-brand border-brand/30' },
  wnba: { label: 'WNBA', cls: 'bg-warn/15 text-warn border-warn/30'    },
};

type SportFilter = 'all' | 'mlb' | 'wnba';

// ── Opportunity card ──────────────────────────────────────────────────────────

interface OpCardProps {
  rec: SportRecommendation;
  isTop: boolean;
  stackIndex: number;
  onDismiss: () => void;
  onExecute: (rec: SportRecommendation) => void;
  onOpen: (rec: SportRecommendation) => void;
}

function OpCard({ rec, isTop, stackIndex, onDismiss, onExecute, onOpen }: OpCardProps) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-8, 8]);
  const rightOpacity = useTransform(x, [20, 100], [0, 1]);
  const leftOpacity  = useTransform(x, [-100, -20], [1, 0]);

  const decision = DECISION_STYLES[rec.decisionStatus];
  const league   = LEAGUE_STYLES[rec.leagueId] ?? { label: rec.leagueId.toUpperCase(), cls: 'bg-surface-3 text-gray-400 border-surface-4' };
  const isBetNow = rec.timing === 'BET_NOW';
  const isStrong = rec.decisionStatus === 'STRONG_BET';
  const confidence = Math.round((rec.confidence ?? 0) * 100);
  const edgePct = (rec.edge * 100).toFixed(1);

  // Non-top cards are just visual backdrop — no drag, just scale/offset
  if (!isTop) {
    const scale = 1 - stackIndex * 0.05;
    const yOffset = stackIndex * 14;
    const opacity = 1 - stackIndex * 0.25;
    return (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `scale(${scale}) translateY(${yOffset}px)`, opacity, zIndex: 10 - stackIndex, transformOrigin: 'bottom center' }}
      >
        <div className={clsx(
          'w-full h-full rounded-2xl border bg-surface-1',
          isStrong ? 'border-win/20' : 'border-brand/20',
        )} />
      </div>
    );
  }

  const handleDragEnd = (_: never, info: { offset: { x: number }; velocity: { x: number } }) => {
    const { offset, velocity } = info;
    if (offset.x > SWIPE_THRESHOLD || velocity.x > SWIPE_VELOCITY) {
      // Swipe right → dismiss
      void animate(x, 500, { type: 'spring', stiffness: 300, damping: 30 });
      setTimeout(onDismiss, 220);
    } else if (offset.x < -SWIPE_THRESHOLD || velocity.x < -SWIPE_VELOCITY) {
      // Swipe left → execute
      void animate(x, -500, { type: 'spring', stiffness: 300, damping: 30 });
      setTimeout(() => onExecute(rec), 220);
    } else {
      void animate(x, 0, { type: 'spring', stiffness: 500, damping: 35 });
    }
  };

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: -300, right: 300 }}
      dragElastic={0.1}
      onDragEnd={handleDragEnd}
      onClick={() => onOpen(rec)}
      style={{ x, rotate, zIndex: 20 }}
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
    >
      {/* Swipe hint overlays */}
      <motion.div
        style={{ opacity: rightOpacity }}
        className="absolute inset-0 rounded-2xl bg-gradient-to-l from-surface-3/80 to-transparent pointer-events-none z-10 flex items-center justify-end pr-6"
      >
        <div className="flex flex-col items-center gap-1">
          <ChevronRight className="w-8 h-8 text-gray-400" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Skip</span>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: leftOpacity }}
        className="absolute inset-0 rounded-2xl bg-gradient-to-r from-gold/20 to-transparent pointer-events-none z-10 flex items-center justify-start pl-6"
      >
        <div className="flex flex-col items-center gap-1">
          <ChevronLeft className="w-8 h-8 text-gold" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-gold">Execute</span>
        </div>
      </motion.div>

      {/* Card content */}
      <div className={clsx(
        'w-full h-full rounded-2xl border bg-surface-1 overflow-hidden flex flex-col',
        isStrong ? 'border-win/30' : 'border-brand/25',
        isBetNow && 'border-gold/40',
      )}>
        {/* Card top band */}
        {isBetNow && (
          <div className="h-1 bg-gradient-to-r from-gold/60 via-gold to-gold/60 flex-shrink-0" />
        )}

        <div className="flex-1 flex flex-col p-5 gap-4 overflow-hidden">
          {/* Badges row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wide', league.cls)}>
              {league.label}
            </span>
            {decision && (
              <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wide', decision.cls)}>
                {decision.label}
              </span>
            )}
            {isBetNow && (
              <span className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border bg-gold/15 text-gold border-gold/40 uppercase tracking-wide">
                <Zap className="w-2.5 h-2.5" />
                BET NOW
              </span>
            )}
          </div>

          {/* Teams */}
          <div className="space-y-1">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-widest">Matchup</p>
            <p className="text-xl font-bold text-gray-100 leading-tight">
              {rec.awayTeamName}
            </p>
            <p className="text-xs text-gray-600 font-medium">vs</p>
            <p className="text-xl font-bold text-gray-100 leading-tight">
              {rec.homeTeamName}
            </p>
          </div>

          {/* Bet details */}
          <div className="rounded-xl border border-surface-4 bg-surface-0/50 p-3.5 space-y-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">Recommendation</p>
            <p className="text-sm font-bold text-gray-100">{rec.selection}</p>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500">{rec.market}</span>
              <span className={clsx('font-mono font-bold tabular-nums', rec.odds > 0 ? 'text-win' : 'text-gray-300')}>
                {rec.odds > 0 ? '+' : ''}{rec.odds}
              </span>
            </div>
          </div>

          {/* Edge + Confidence */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-surface-4 bg-surface-0/50 p-3 space-y-1.5">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">Edge</p>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5 text-win" />
                <span className="font-mono text-base font-bold text-win">+{edgePct}%</span>
              </div>
            </div>
            <div className="rounded-xl border border-surface-4 bg-surface-0/50 p-3 space-y-1.5">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-gray-600">Confidence</p>
              <p className={clsx('font-mono text-base font-bold tabular-nums', confidence >= 70 ? 'text-win' : confidence >= 55 ? 'text-warn' : 'text-gray-400')}>
                {confidence}%
              </p>
              <div className="h-1 bg-surface-3 rounded-full overflow-hidden mt-1">
                <div
                  className={clsx('h-full rounded-full', confidence >= 70 ? 'bg-win' : confidence >= 55 ? 'bg-warn' : 'bg-gray-500')}
                  style={{ width: `${confidence}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-surface-4/60 flex items-center justify-center">
          <p className="text-[9px] text-gray-700 uppercase tracking-widest">Tap for full intelligence</p>
        </div>
      </div>
    </motion.div>
  );
}

// ── MobileOpportunityStack ────────────────────────────────────────────────────

interface MobileOpportunityStackProps {
  open: boolean;
  onClose: () => void;
}

export function MobileOpportunityStack({ open, onClose }: MobileOpportunityStackProps) {
  const { openWorkspaceDrawer, openActionSheet } = useAppStore();
  const [queue, setQueue] = useState<SportRecommendation[]>([]);
  const [allPicks, setAllPicks] = useState<SportRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [sportFilter, setSportFilter] = useState<SportFilter>('all');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    pulseApi.getPicks()
      .then((data) => {
        const actionable = data
          .filter((r) => r.decisionStatus === 'STRONG_BET' || r.decisionStatus === 'QUALIFIED_BET')
          .sort((a, b) => {
            const ta = a.timing === 'BET_NOW' ? 0 : 1;
            const tb = b.timing === 'BET_NOW' ? 0 : 1;
            if (ta !== tb) return ta - tb;
            const da = a.decisionStatus === 'STRONG_BET' ? 0 : 1;
            const db = b.decisionStatus === 'STRONG_BET' ? 0 : 1;
            if (da !== db) return da - db;
            return b.edge - a.edge;
          });
        setAllPicks(actionable);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const filtered = sportFilter === 'all'
      ? allPicks
      : allPicks.filter((r) => r.leagueId === sportFilter);
    setQueue(filtered);
  }, [allPicks, sportFilter]);

  if (!open) return null;

  const handleDismiss = () => setQueue((q) => q.slice(1));
  const handleExecute = (rec: SportRecommendation) => {
    openActionSheet(rec);
    setQueue((q) => q.slice(1));
  };
  const handleOpen = (rec: SportRecommendation) => openWorkspaceDrawer(rec);

  const handleReset = () => {
    const filtered = sportFilter === 'all'
      ? allPicks
      : allPicks.filter((r) => r.leagueId === sportFilter);
    setQueue(filtered);
  };

  const current = queue[0];
  const betNowCount = queue.filter((r) => r.timing === 'BET_NOW').length;

  return (
    <div className="fixed inset-0 z-[52] md:hidden flex flex-col bg-surface-0/97 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-safe-top pb-3 border-b border-surface-4 bg-surface-1/50 flex-shrink-0"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Zap className="w-4 h-4 text-gold flex-shrink-0" />
          <p className="text-sm font-bold text-gray-100">Opportunities</p>
          {betNowCount > 0 && (
            <span className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-gold/15 text-gold border border-gold/40 uppercase">
              <span className="w-1 h-1 rounded-full bg-gold animate-ping" />
              {betNowCount} BET NOW
            </span>
          )}
          {queue.length > 0 && (
            <span className="text-[10px] text-gray-600 font-mono ml-auto">{queue.length} left</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-surface-2 transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Sport filter */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-surface-4/60 flex-shrink-0">
        {(['all', 'mlb', 'wnba'] as SportFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setSportFilter(f)}
            className={clsx(
              'px-3 py-1 text-[10px] font-semibold rounded-lg uppercase tracking-wide transition-colors',
              sportFilter === f
                ? 'bg-surface-2 border border-surface-3 text-gray-200'
                : 'text-gray-600 hover:text-gray-300',
            )}
          >
            {f === 'all' ? 'All Sports' : f.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Card stack area */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-4 relative overflow-hidden min-h-0">
        {loading ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <Activity className="w-8 h-8 text-gray-600 animate-pulse" />
            <p className="text-sm text-gray-500">Scanning for opportunities…</p>
          </div>
        ) : queue.length === 0 ? (
          <div className="flex flex-col items-center gap-4 text-center max-w-xs">
            <div className="w-16 h-16 rounded-2xl bg-surface-2 border border-surface-4 flex items-center justify-center">
              <Activity className="w-7 h-7 text-gray-600" />
            </div>
            <div>
              <p className="text-base font-bold text-gray-300">No Opportunities</p>
              <p className="text-sm text-gray-600 mt-1">All agents are monitoring the markets. Check back soon.</p>
            </div>
            {allPicks.length > 0 && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-surface-2 border border-surface-3 text-sm text-gray-300 hover:text-gray-100 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Review Again ({allPicks.length})
              </button>
            )}
          </div>
        ) : (
          <div className="relative w-full" style={{ height: 'min(480px, 72vh)' }}>
            <AnimatePresence>
              {queue.slice(0, 3).map((rec, i) => (
                <OpCard
                  key={rec.id}
                  rec={rec}
                  isTop={i === 0}
                  stackIndex={i}
                  onDismiss={handleDismiss}
                  onExecute={handleExecute}
                  onOpen={handleOpen}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Swipe hints */}
      {queue.length > 0 && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-t border-surface-4/40"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
          <div className="flex items-center gap-1.5 text-gold">
            <ChevronLeft className="w-4 h-4" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">Execute</span>
          </div>
          <span className="text-[9px] text-gray-700 uppercase tracking-widest">Swipe to decide</span>
          <div className="flex items-center gap-1.5 text-gray-500">
            <span className="text-[10px] font-semibold uppercase tracking-wide">Skip</span>
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      )}
    </div>
  );
}
